import { EmptyStateIcons, Table } from '@silverfort/sf-components';
import { LogEntry } from '../../types';
import { LOGS_TABLE_COLUMNS } from './consts';
import { LogsTableRow } from './types';
import { FC, ReactNode } from 'react';
import { generateHeadline } from './utils';
import { ExpandedLogContent } from './ExpandedLogContent';
import { getExtraRiskIndicatorDescription, getRuleIdWithPrefix } from '@silverfort-nodelibs/common/utils/common';
import { AUTHENTICATION_LOG_REASONS } from '@silverfort-nodelibs/common/utils/consts';
import { isEmpty } from 'lodash';
import { getRiskIndicatorByRiskRule } from '@silverfort-nodelibs/common/utils/insight';

interface LogsTableProps {
    logs: LogEntry[];
    defaultDestinations: Record<string, string>;
    loading: boolean;
    title: string;
    count: number;
    wallNames: Record<string, string>;
    hiddenRiskIndicators: string[];
}

const ROW_HEIGHT = 48;

const logKeyExtractor = (log: LogsTableRow) => log.id;

const getRiskIndicatorTitle = (riskIndicatorObj, logData) => {
    let riskIndicatorTitle = riskIndicatorObj.authTitle;
    const extraDetails =
        !isEmpty(riskIndicatorObj.extraLogDetails) &&
        getExtraRiskIndicatorDescription(
            logData,
            riskIndicatorObj.extraLogDetails.extraDetailsType,
            riskIndicatorObj.extraLogDetails.extraDetailsField
        );

    if (!isEmpty(extraDetails)) {
        riskIndicatorTitle += ` (${extraDetails})`;
    }
    return riskIndicatorTitle;
};

const convertLogsToRows = (
    logs: (LogEntry & { headline: string | ReactNode })[],
    wallNames: Record<string, string>,
    hiddenRiskIndicators: string[]
): LogsTableRow[] => {
    return logs.map((log) => {
        const wallName = wallNames[log.wallId];
        const ruleIdWithPrefix =
            log.effectiveRuleId && log.effectiveRuleType
                ? getRuleIdWithPrefix(log.effectiveRuleId, log.effectiveRuleType)
                : '';

        const isNtlmFailOpen =
            log.protocolType?.toLowerCase() === 'ntlm' &&
            log.userResponse?.toLowerCase() === AUTHENTICATION_LOG_REASONS.FAIL_OPEN.USER_RESPONSE &&
            !ruleIdWithPrefix;

        const filteredRiskIndicators = log.riskIndicatorsV2
            ? log.riskIndicatorsV2.filter((riskIndicator) => !hiddenRiskIndicators.includes(riskIndicator))
            : [];

        const riskIndicatorsList = filteredRiskIndicators.flatMap((riskRule) => {
            const riskIndicator = getRiskIndicatorByRiskRule(riskRule);
            return riskIndicator ? riskIndicator : [];
        });

        const riskIndicators = riskIndicatorsList.map((riskIndicatorObj) => {
            return getRiskIndicatorTitle(riskIndicatorObj, log);
        });
        return {
            id: log.id,
            date: new Date(log.timestamp),
            type: log.directoryType,
            headline: log.headline,
            expandedData: (
                <ExpandedLogContent
                    log={log}
                    wallName={wallName}
                    isNtlmFailOpen={isNtlmFailOpen}
                    riskIndicators={riskIndicators}
                />
            )
        };
    });
};

export const LogsTable: FC<LogsTableProps> = ({
    logs,
    defaultDestinations,
    loading,
    title,
    count,
    wallNames,
    hiddenRiskIndicators
}) => {
    const rowsWithHeadline = logs.map((log) => ({
        ...log,
        headline: generateHeadline({
            data: log,
            defaultDestinations
        })
    }));

    const rows = convertLogsToRows(rowsWithHeadline, wallNames, hiddenRiskIndicators);

    return (
        <div className="flex flex-col gap-2">
            {loading ? (
                <div className="animate-pulse h-5 w-full rounded bg-sf-gray-50"></div>
            ) : (
                <span className="text-13 underline">{`${title} (${count})`}</span>
            )}
            <Table
                loading={loading}
                tableContainerClassName="overflow-x-hidden no-scrollbar min-h-[250px] max-h-[calc(100vh-232px)]"
                columns={LOGS_TABLE_COLUMNS}
                rows={rows}
                keyExtractor={logKeyExtractor}
                expandableRowsMode
                expandedDataKey="expandedData"
                bodyHeight={Math.min(ROW_HEIGHT * rows.length, 368)}
                selectable={false}
                EmptyStateProps={{ title: 'No Logs', icon: EmptyStateIcons.NoItems }}
            />
        </div>
    );
};
